'use client';

import { Box, MenuItem, TextField, type Theme, useMediaQuery, useTheme } from '@mui/material';
import { FooterSocialMediaShare } from './FooterSocialMediaShare';
import { FooterTermsLinks } from './FooterTermsLinks';
import { useComputedColor } from '@/hooks/useComputedColor';
import { useLanguageStore } from '@/infrastructure/zustand/languageStoreProvider';
import { useCorporateStore } from '@/infrastructure/zustand/corporateStoreProvider';
import { useMoneyStore } from '@/infrastructure/zustand/moneyStoreProvider';
import type { Language } from '@/shared';
import useClientOnly from '@/hooks/useClientOnly';
import { useRouter } from 'next/router';
import type { CampaignConfig, IndexConfig } from '@/modules/config.interface';
import { transformToLocale } from '@/infrastructure/zustand/languageStore';

export const useFooterSx = (color: string, theme: Theme) => ({
	textField: {
		'borderRadius': '5px',
		'textAlign': 'left',
		color,
		'& .MuiInputBase-root': {
			'fontSize': '12px',
			'height': '30px',
			'& .MuiSvgIcon-root': {},
			[theme.breakpoints.up('sm')]: {
				fontSize: '14px',
				color,
			},
		},
		'& .MuiOutlinedInput-root': {
			'& fieldset, &:hover fieldset': {
				borderColor: color,
			},
			'&.Mui-focused fieldset': {
				borderColor: color,
			},
		},
		'& .MuiSelect-outlined.MuiSelect-outlined': {
			paddingRight: '16px',
			paddingLeft: '5px',
			color,
			[theme.breakpoints.up('sm')]: {
				paddingRight: '32px',
				paddingLeft: '14px',
			},
			[theme.breakpoints.up('md')]: {
				paddingLeft: '8px',
				paddingRight: '26px',
			},
		},
		'& .MuiSelect-iconOutlined': {
			right: '-4px',
			color,
			[theme.breakpoints.up('sm')]: {
				right: '1px',
			},
		},
	},
	container: {
		position: 'fixed',
		bottom: 0,
		left: 0,
		right: 0,
		borderTop: '1px #ddd solid',
		[theme.breakpoints.up('md')]: {
			width: '50%',
		},
	},
	text: {
		'fontSize': '12px',
		'lineHeight': '12px',
		'&:hover': {
			cursor: 'pointer',
		},
	},
});

export const Footer = ({ config }: { config: IndexConfig | CampaignConfig }) => {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
	const currentLangCode = useLanguageStore((state) => state.currentLangCode);
	const changeLanguage = useLanguageStore((state) => state.changeLanguage);
	const languages = useCorporateStore((state) => state.metadata.languages);
	const currencies = useCorporateStore((state) => state.metadata.currencies);
	const currentCurrencyCode = useMoneyStore((state) => state.currentCurrencyCode);
	const changeCurrency = useMoneyStore((state) => state.changeCurrency);
	const corporateTexts = config.metadata.textsCollection.texts;
	const clientOnly = useClientOnly();
	const router = useRouter();
	const { pathname, asPath, query } = router;

	const computedBackgroundColor = config.footerBackgroundColor ?? '#eaeaea';

	const computedColor = useComputedColor(config.footerTextColor, computedBackgroundColor);
	const sx = useFooterSx(computedColor, theme);
	const languageSelectorTraduction = {
		ENG: {
			ENG: 'ENG',
			SPA: 'SPA',
			ITA: 'ITA',
			CAT: 'CAT',
			EUS: 'BAS',
			GLG: 'GAL',
			GER: 'GER',
			FRE: 'FRE',
			KOR: 'KOR',
			CHI: 'CHI',
			JPN: 'JAP',
			POR: 'POR',
			RUS: 'RUS',
			DUT: 'DUT',
			GRE: 'GRE',
		},
		SPA: {
			ENG: 'ING',
			SPA: 'ESP',
			ITA: 'ITA',
			CAT: 'CAT',
			EUS: 'EUS',
			GLG: 'GAL',
			GER: 'ALE',
			FRE: 'FRA',
			KOR: 'COR',
			CHI: 'CHI',
			JPN: 'JAP',
			POR: 'POR',
			RUS: 'RUS',
			DUT: 'HOL',
			GRE: 'GRI',
		},
		ITA: {
			ENG: 'ING',
			SPA: 'SPA',
			CAT: 'CAT',
			ITA: 'ITA',
			EUS: 'BAS',
			GLG: 'GAL',
			GER: 'TED',
			FRE: 'FRA',
			KOR: 'COR',
			CHI: 'CIN',
			JPN: 'GIA',
			POR: 'POR',
			RUS: 'RUS',
			DUT: 'OLA',
			GRE: 'GRE',
		},
		CAT: {
			ENG: 'ANG',
			SPA: 'ESP',
			ITA: 'ITA',
			CAT: 'CAT',
			EUS: 'EUS',
			GLG: 'GAL',
			GER: 'ALE',
			FRE: 'FRA',
			KOR: 'COR',
			CHI: 'XIN',
			JPN: 'JAP',
			POR: 'POR',
			RUS: 'RUS',
			DUT: 'NEE',
			GRE: 'GRE',
		},
		EUS: {
			ENG: 'ANG',
			SPA: 'ESP',
			ITA: 'ITA',
			CAT: 'CAT',
			EUS: 'EUS',
			GLG: 'GAL',
			GER: 'ALE',
			FRE: 'FRA',
			KOR: 'KOR',
			CHI: 'TXI',
			JPN: 'JAP',
			POR: 'POR',
			RUS: 'ERR',
			DUT: 'HOL',
			GRE: 'GRE',
		},
		GLG: {
			ENG: 'ANG',
			SPA: 'ESP',
			ITA: 'ITA',
			CAT: 'CAT',
			EUS: 'EUS',
			GLG: 'GAL',
			GER: 'ALE',
			FRE: 'FRA',
			KOR: 'COR',
			CHI: 'CHI',
			JPN: 'XAP',
			POR: 'POR',
			RUS: 'RUS',
			DUT: 'HOL',
			GRE: 'GRE',
		},
		GER: {
			ENG: 'ENG',
			SPA: 'SPA',
			ITA: 'ITA',
			CAT: 'KAT',
			EUS: 'BAS',
			GLG: 'GAL',
			GER: 'DEU',
			FRE: 'FRA',
			KOR: 'KOR',
			CHI: 'CHI',
			JPN: 'JAP',
			POR: 'POR',
			RUS: 'RUS',
			DUT: 'NIE',
			GRE: 'GRI',
		},
		FRE: {
			ENG: 'ANG',
			SPA: 'ESP',
			ITA: 'ITA',
			CAT: 'CAT',
			EUS: 'BAS',
			GLG: 'GAL',
			GER: 'ALL',
			FRE: 'FRA',
			KOR: 'COR',
			CHI: 'CHI',
			JPN: 'JAP',
			POR: 'POR',
			RUS: 'RUS',
			DUT: 'NEE',
			GRE: 'GRE',
		},
		KOR: {
			ENG: '영어',
			SPA: '스페인의',
			ITA: '이탈리아 사람',
			CAT: '카탈로니아 사람',
			EUS: '바스크 사람',
			GLG: '갈리시아어',
			GER: '독일 사람',
			FRE: '프랑스 국민',
			KOR: '한국인',
			CHI: '중국인',
			JPN: '일본어',
			POR: '포르투갈 인',
			RUS: '러시아인',
			DUT: '네덜란드 사람',
			GRE: '그리스 어',
		},
		CHI: {
			ENG: '英语',
			SPA: '西班牙语',
			ITA: '意大利语',
			CAT: '加泰罗尼亚语',
			EUS: '巴斯克',
			GLG: '加利西亚语',
			GER: '德语',
			FRE: '法語',
			KOR: '韩国人',
			CHI: '中国人',
			JPN: '日本人',
			POR: '葡萄牙语',
			RUS: '俄语',
			DUT: '荷蘭語',
			GRE: '希臘文',
		},
		JPN: {
			ENG: '英語',
			SPA: 'スペイン語',
			ITA: 'イタリアの',
			CAT: 'カタルーニャ語',
			EUS: 'バスク語',
			GLG: 'ガリシア語',
			GER: 'ドイツ人',
			FRE: 'フランス語',
			KOR: '韓国語',
			CHI: '中国語',
			JPN: '日本語',
			POR: 'ポルトガル語',
			RUS: 'ロシア',
			DUT: 'オランダの',
			GRE: 'ギリシャ語',
		},
		POR: {
			ENG: 'ING',
			SPA: 'ESP',
			ITA: 'ITA',
			CAT: 'CAT',
			EUS: 'BAS',
			GLG: 'GAL',
			GER: 'ALE',
			FRE: 'FRA',
			KOR: 'COR',
			CHI: 'CHI',
			JPN: 'JAP',
			POR: 'POR',
			RUS: 'RUS',
			DUT: 'HOL',
			GRE: 'GRE',
		},
		RUS: {
			ENG: 'Английский',
			SPA: 'испанский',
			ITA: 'итальянский',
			CAT: 'каталонский',
			EUS: 'Баскский',
			GLG: 'галисийский',
			GER: 'Немецкий',
			FRE: 'Французский',
			KOR: 'Корейский',
			CHI: 'Китайский',
			JPN: 'Японский',
			POR: 'португальский',
			RUS: 'Русский',
			DUT: 'голландский',
			GRE: 'греческий',
		},
		DUT: {
			ENG: 'ENG',
			SPA: 'SPA',
			ITA: 'ITA',
			CAT: 'CAT',
			EUS: 'BAS',
			GLG: 'GAL',
			GER: 'DUI',
			FRE: 'FRA',
			KOR: 'KOR',
			CHI: 'CHI',
			JPN: 'JAP',
			POR: 'POR',
			RUS: 'RUS',
			DUT: 'NED',
			GRE: 'GRI',
		},
		GRE: {
			ENG: 'Αγγλικά',
			SPA: 'Ισπανικά',
			ITA: 'ιταλικός',
			CAT: 'καταλανικά',
			EUS: 'Βάσκος',
			GLG: 'Γαλικιανός',
			GER: 'Γερμανός',
			FRE: 'γαλλική γλώσσα',
			KOR: 'κορεάτης',
			CHI: 'κινέζικα',
			JPN: 'Ιαπωνικά',
			POR: 'Πορτογαλικά',
			RUS: 'Ρωσική',
			DUT: 'Ολλανδός',
			GRE: 'Ελληνικά',
		},
	};

	return (
		<Box
			display="flex"
			style={{ backgroundColor: computedBackgroundColor }}
			sx={sx.container}
			width={1}
			padding={'5px 20px'}
			alignItems="center"
			zIndex="drawer"
		>
			<Box display="flex" width={1 / 3}>
				<Box style={{ marginRight: '5px' }} width={`${isMobile ? 1 / 2 : '65px'}`}>
					<TextField
						select
						value={currentLangCode}
						name="lang"
						variant="outlined"
						onChange={(e) => {
							const value = e.target.value as Language;

							document.cookie = `NEXT_LOCALE=${transformToLocale(value)}; max-age=31536000; path=/`;
							router.push({ pathname, query }, asPath).then(() => changeLanguage(value));
						}}
						sx={sx.textField}
						InputLabelProps={{
							style: { color: computedColor },
						}}
					>
						{languages.map((code, i) => (
							<MenuItem key={i + code} value={code}>
								{languageSelectorTraduction[currentLangCode][code]}
							</MenuItem>
						))}
					</TextField>
				</Box>
				<Box style={{ marginRight: '5px' }} width={`${isMobile ? 1 / 2 : '65px'}`}>
					{clientOnly && (
						<TextField
							select
							value={currentCurrencyCode}
							variant="outlined"
							onChange={(e) => changeCurrency(e.target.value)}
							name="currency"
							sx={sx.textField}
							InputLabelProps={{
								style: { color: computedColor },
							}}
						>
							{currencies.map((c) => (
								<MenuItem key={c.symbol + c.code} value={c.code}>
									{c.code} - {c.symbol}
								</MenuItem>
							))}
						</TextField>
					)}
				</Box>
			</Box>

			<FooterTermsLinks
				color={computedColor}
				termsAndConditions={corporateTexts?.termsAndConditions}
				privacyNotice={corporateTexts?.privacyNotice}
			/>

			<FooterSocialMediaShare color={computedColor} tagLine={corporateTexts?.tagLine} title={corporateTexts?.title} />
		</Box>
	);
};
